import type { ReactNode } from 'react'

import { Heading } from '@adverity/design-system/components/heading'
import { Stack } from '@adverity/design-system/components/stack'
import { Button } from '@adverity/ds-component/button'
import { Separator } from '@adverity/ds-component/separator'
import { IconArrowLeft } from '@adverity/ds-icon'
import { StaticImage } from '@adverity/image'
import { getPathTo } from '@adverity/legacy-datatap-paths/get-path-to'
import { keys } from 'remeda'

const errorContent = {
    '401': {
        title: 'Authentication Required',
        content: "You don't have permission to access this page",
        Image: '/403.svg',
    },
    '403': {
        title: 'Not Authorized',
        content: "You don't have permission to access this area",
        Image: '/403.svg',
    },
    '404': {
        title: 'Not found',
        content: 'We could not find what you are looking for',
        Image: '/404.svg',
    },
    '410': {
        title: 'Not found',
        content: 'We could not find what you are looking for',
        Image: '/404.svg',
    },
    '500': {
        title: 'System Failure',
        content: 'Sorry, something went wrong on our end',
        Image: '/500.svg',
    },
    '503': {
        title: 'Service Unavailable',
        content: 'Sorry, something went wrong on our end',
        Image: '/500.svg',
    },
} as const

type SupportedErrorCodes = keyof typeof errorContent

const getErrorPageTitle = (errorCode: SupportedErrorCodes) => `${errorCode} | ${errorContent[errorCode].title}`

const errorCodes = keys(errorContent)

const DEFAULT_ERROR_CODE = '404'

const getErrorPageVariant = (errorCode?: string) => {
    if (errorCode && errorCodes.includes(errorCode as (typeof errorCodes)[number])) {
        return errorCode as (typeof errorCodes)[number]
    }

    return errorCode?.toString().startsWith('5') ? '500' : DEFAULT_ERROR_CODE
}

const textProps = { display: 'block', textAlign: 'center' } as const
const ErrorContent = ({ code, children }: { code: SupportedErrorCodes; children?: ReactNode }) => {
    const IllustrationImage = errorContent[code].Image

    const title = getErrorPageTitle(code)

    return (
        <div className="p-xx-small flex size-full grow items-center justify-center">
            {/* <PageHead title={title} /> */}
            <Stack spacing="medium" alignItems="center" maxWidth={600}>
                <div className="w-full max-w-[300px]">
                    <StaticImage src={IllustrationImage} alt={title} width={300} height={300} />
                </div>
                <div className="items-center">
                    <Heading level={3} {...textProps} as="p">
                        {code}
                    </Heading>
                    <Stack spacing="medium" alignItems="center">
                        <Heading
                            level={1}
                            {...textProps}
                            as="h1"
                            marginTop="xx-small"
                            marginBottom="xx-small"
                            fontWeight="bold"
                        >
                            {errorContent[code].title}
                        </Heading>
                        <Heading level={3} {...textProps} as="p">
                            {errorContent[code].content}
                        </Heading>
                    </Stack>
                </div>
                {children}
            </Stack>
        </div>
    )
}

export const ErrorPage = ({
    errorCode,
    action = (
        <Button asChild prefix={<IconArrowLeft />}>
            <a href={getPathTo.home()}>Go Back Home</a>
        </Button>
    ),
}: {
    errorCode?: string
    action?: ReactNode
}) => {
    const supportedErrorCode = getErrorPageVariant(errorCode)

    return (
        <ErrorContent code={supportedErrorCode}>
            <Separator />
            <Stack orientation="horizontal" justifyContent="center" spacing="small">
                {action}
                <Button variant="secondary" asChild>
                    <a
                        href={`mailto:support@adverity.com?subject=Support%20%7C%20${errorCode}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Contact support
                    </a>
                </Button>
            </Stack>
        </ErrorContent>
    )
}
